/**
 * KIR-2788: a notification deep link is `/{org}/alerts/{token}` — one opaque
 * token (a finding_key or an alert-set id) — and the agent appends the same
 * filter query the old filtered-list link used (severity / type / lastSeen /
 * client / visibility). When the token resolves, the landing view renders the
 * exact alert(s) and ignores the query. When it can't be resolved (purged
 * finding, expired or not-yet-written alert set, wrong scope), we degrade to the
 * alerts list *carrying that query* — so the user lands on the filtered "summary"
 * view instead of a bare list. When there's no query (older agent), it's the
 * plain list, exactly as before.
 */
export function alertsListFallbackPath(
  organizationId: string,
  searchParams: URLSearchParams
): string {
  const query = searchParams.toString();
  return query
    ? `/${organizationId}/alerts?${query}`
    : `/${organizationId}/alerts`;
}

/**
 * KIR-2788: the personal (account) equivalent — a no-org user's notification
 * deep link is `/dashboard/alerts/{token}`, so an unresolvable token degrades to
 * the personal alerts list, carrying any filter query the same way.
 */
export function accountAlertsListFallbackPath(
  searchParams: URLSearchParams
): string {
  const query = searchParams.toString();
  return query ? `/dashboard/alerts?${query}` : '/dashboard/alerts';
}

/**
 * KIR-4320 fallback-query detection (KIR-4664).
 *
 * The agent appends the old filtered-list query to a notification deep link so
 * an unresolvable token can still land the user on the relevant *filtered* list
 * (severity / type / lastSeen / client / visibility). Those params are the
 * DataTable's persisted filter encoding: `<tableId>.<key>[<operator>]=...`
 * (e.g. `org-alt.sev[includes]=high`, `user-alt.ls[between]=...`) plus the bare
 * `visibility` param.
 *
 * This predicate decides the empty/expired branch in {@link ScopedAlertsView}:
 *   - token resolved to nothing / unresolved AND **useful filter params present**
 *     → degrade to the filtered list (as today), so the user still sees the
 *       relevant alerts.
 *   - token resolved to nothing / unresolved AND **no useful filter params**
 *     (older agent, or a bare token) → show the expired-alert splash instead of
 *     a silent redirect to a bare, unfiltered list.
 *
 * Only a CONTENT filter — a rule **type** (`t[includes]`) or a first-seen window
 * (`fs[between]`) — marks a reconstructable multi-alert slice worth degrading to.
 * The severity (`sev`), client (`ct`), the bracket-less `visibility` view-mode,
 * AND the last-seen window (`ls[between]`) ride on EVERY Go-agent notification
 * deep link, single-alert links included: `withFilteredDeepLink` stamps a
 * `from`/`to` window into `safe` on all three branches (IncidentID, single
 * finding_key, composite), so `ls` is present even on an expired single alert.
 * Counting `ls` (KIR-4664 P2) made this predicate always true for native-agent
 * links, so `ExpiredAlertSplash` never rendered — the view always redirected to
 * the filtered list. We therefore match only `t`/`fs` (the DataTable encoding
 * `<tableId>.<field>[<op>]=…`, across both `org-alt` and `user-alt` prefixes); a
 * lone severity / client / visibility / last-seen window is not "useful" and
 * correctly routes to the splash. The window-end summary, which additionally
 * stamps rule types (`t[includes]` via `summaryFilterParams`), still degrades to
 * the filtered list.
 */
export function hasDeepLinkFilterParams(
  searchParams: URLSearchParams
): boolean {
  for (const key of searchParams.keys()) {
    // Field name before the operator bracket, e.g. "org-alt.t" from
    // "org-alt.t[includes]". `ls` (last-seen window) is deliberately excluded: the
    // agent stamps it on every link, single-alert included (see doc above), so
    // counting it would send every expired native-agent alert to the filtered list
    // and the splash would never render.
    const field = key.split('[')[0];
    if (/\.(t|fs)$/.test(field)) {
      return true;
    }
  }
  return false;
}
